import { CounterProps } from '@engyalo/delivery-ui-components/lib/ui/components/Counter'
import { ItemCardCounterProps } from '@engyalo/delivery-ui-components/lib/ui/components/ItemCard'
import deepMerge from '../../../../utils/deepMerge'
import {
  CinnamonActionNames,
  Referrers,
  TemplateActionNames,
  TextLibrary,
} from '../../defaultConstants'

export const CounterDefaultArgs = {
  sku: '{{this.data.sku}}',
  value: '{{this.value}}',
  newValue: '{{this.newValue}}',
  product: '{{this.data}}',
  resultFixed: '{{this.resultFixed}}',
  currency:
    '{{config.options.currencyPrefix}} || {{config.options.currencyCode}}',
  isFromCounterButtons: true,
}

export const CounterBundleArgs = {
  sku: '{{this.data.sku}}',
  value: '{{this.value}}',
  newValue: '{{this.newValue}}',
  product: '{{this.data}}',
  currency:
    '{{config.options.currencyPrefix}} || {{config.options.currencyCode}}',
  isBundle: true,
  newSubItems: '{{this.data.subItem}}',
  currentSubItems: '{{this.data.subItemsProduct}}',
}

export const CounterProductDetailArgs = {
  ...CounterDefaultArgs,
  sku: '{{params.sku}}',
  product: '{{catalog.selectedProduct}}',
}

export const CounterPackageArgs = {
  sku: '{{this.data.sku}}',
  value: '{{this.value}}',
  newValue: '{{this.newValue}}',
  product: '{{this.data}}',
  resultFixed: '{{this.resultFixed}}',
  currency:
    '{{config.options.currencyPrefix}} || {{config.options.currencyCode}}',
  isPackage: true,
}

export const RULE_INVALIDATED_BY_PATH = '{{this.ruleInvalidatedBy}}'

const CounterInputChangeArgs = {
  originalValue: '{{this.originalValue}}',
  // The typed number after the display modifier is divided out. `originalValue` is
  // what was keyed in, which is a different unit whenever `modifierMap` is in play,
  // so anything compared against a quantity limit has to use this one.
  modifiedValue: '{{this.modifiedValue}}',
  inRangeValue: '{{this.inRangeValue}}',
  resultFixed: '{{this.resultFixed}}',
  resultValue: '{{this.resultValue}}',
  ruleInvalidatedBy: RULE_INVALIDATED_BY_PATH,
  nextValidValue: '{{this.nextValidValue}}',
  prevValidValue: '{{this.prevValidValue}}',
  // Whether the counter rejected the typed value. Forwarded so the cart can act on
  // what the counter actually decided instead of inferring it from the value alone.
  isValueOutOfRange: '{{this.isValueOutOfRange}}',
  // The ceiling the counter validated against, and whether it already clamped the
  // typed value to it. The cart re-applies the cap after its own rounding stages
  // and names the number in the max-quantity alert, so it needs the same figure
  // the counter used rather than one re-derived from the product.
  //
  // Forwarded by @engyalo/delivery-ui-components from the release that adds
  // COM-653; on any earlier build these resolve to their literal template strings
  // and the cart falls back to the product's own stock. Every consumer of them
  // guards for that, so the two halves can ship independently.
  cap: '{{this.cap}}',
  isClampedToCap: '{{this.isClampedToCap}}',
  // Which limit produced that cap. The add path uses it to decide whether a
  // fixed result is a stock problem worth reporting as one.
  capSource: '{{this.capSource}}',
}

const CounterDefault: ItemCardCounterProps = {
  step: '{{globals.counter.step}}' as any,
  max: '{{globals.counter.max}}' as any,
  activeInput: '{{globals.counter.activeInput}}' as any,
  modifierMap: '{{globals.counter.modifierMap}}' as any,
  updateValueOnDisabled: false,
  allowSetMaxvalue: '{{globals.counter.allowRetryWhenOutOfStock}}' as any,
  clampTypedInputToCap: '{{globals.counter.clampTypedInputToCap}}' as any,
  allowTypedZero: '{{globals.counter.allowTypedZero}}' as any,
  disabledActions: {
    cinnamonActions: [
      {
        name: CinnamonActionNames.OPEN_DIALOG,
        args: {
          icon: {
            name: 'MdCheck',
          },
          title: TextLibrary.PRODUCT_STOCK_TITLE,
          message: TextLibrary.PRODUCT_STOCK_MESSAGE,
          // Used in place of the two above when the counter stopped at the
          // product's purchase ceiling instead of at inventory, so a customer at
          // their maximum is not told the product is out of stock. `capSource` is
          // forwarded from the delivery-ui-components release that adds COM-653;
          // before that it resolves to its literal placeholder, which matches no
          // known source, so the configured stock wording is used unchanged.
          capSource: '{{this.capSource}}',
          maxQtyTitle: TextLibrary.PRODUCT_MAX_QTY_TITLE,
          maxQtyMessage: TextLibrary.PRODUCT_MAX_QTY_MESSAGE,
          buttons: [
            {
              label: TextLibrary.ACTIONS_ACCEPT,
              actions: {
                cinnamonActions: [
                  {
                    name: CinnamonActionNames.CLOSE_DIALOG,
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  } as any,
}

export const CounterForList: ItemCardCounterProps = deepMerge(CounterDefault, {
  itemPropAsMin: 'minQtyAllowed' as any,
  // Names the item field ItemCard reads the per-product cap from. Kept as a
  // binding rather than baked into ItemCard so the cap can be switched off here,
  // in cinnamon, without a library release. Honoured from the delivery-ui-components
  // release that adds COM-653; earlier builds ignore the prop, so no cap applies.
  itemPropAsMax: 'maxQty' as any,
  minusButton: {
    styles: {
      padding: '4px',
    },
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
          args: CounterDefaultArgs,
        },
      ],
    } as any,
  },
  plusButton: {
    styles: {
      padding: '4px',
    },
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.ADD_ITEM_TO_CART,
          args: CounterDefaultArgs,
        },
      ],
    } as any,
  },
  onInputChange: {
    cinnamonActions: [
      {
        name: CinnamonActionNames.UPDATE_ITEMS_INPUT,
        args: {
          product: '{{this.data}}',
          jumpQty: '{{this.data.step}}',
          ...CounterInputChangeArgs,
        },
      },
    ],
  } as any,
})

export const CounterForListBundle: ItemCardCounterProps = deepMerge(
  CounterDefault,
  {
    itemPropAsMin: 'minQtyAllowed' as any,
    itemPropAsMax: 'maxQty' as any,
    minusButton: {
      styles: {
        padding: '4px',
      },
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
            args: CounterBundleArgs,
          },
        ],
      } as any,
    },
    plusButton: {
      styles: {
        padding: '4px',
      },
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.ADD_ITEM_TO_CART,
            args: CounterBundleArgs,
          },
        ],
      } as any,
    },
  },
)

export const CounterProductDetail: CounterProps = deepMerge(CounterDefault, {
  value: '{{catalog.selectedProduct.quantity}}' as any,
  disabled: '{{catalog.selectedProduct.isSyncing}}' as any,
  customStep: '{{catalog.selectedProduct.step}}' as any,
  roundingRules: '{{catalog.selectedProduct.roundingRules}}' as any,
  stock: '{{catalog.selectedProduct.stock}}' as any,
  min: '{{catalog.selectedProduct.minQtyAllowed}}' as any,
  minQty: '{{catalog.selectedProduct.minQty}}' as any,
  // Bound explicitly because this counter is standalone. The list counters get it
  // from ItemCard, which reads it off the item through the `itemPropAsMax` binding
  // — the same way it reads the minimum through `itemPropAsMin`, and deliberately
  // NOT the way it reads `minQty`/`stock`, which it takes off the item directly.
  // Dropping that binding is the documented off switch and fails open to no cap,
  // so it cannot be removed on the assumption the field is picked up by itself.
  maxQty: '{{catalog.selectedProduct.maxQty}}' as any,
  modifierName: '{{catalog.selectedProduct.packageType}}' as any,
  minusButton: {
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
          args: CounterProductDetailArgs,
        },
      ],
    } as any,
  },
  plusButton: {
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.ADD_ITEM_TO_CART,
          args: CounterProductDetailArgs,
        },
      ],
    } as any,
  },
  onInputChange: {
    cinnamonActions: [
      {
        name: CinnamonActionNames.UPDATE_ITEMS_INPUT,
        args: {
          ...CounterInputChangeArgs,
          product: '{{catalog.selectedProduct}}',
          jumpQty: '{{catalog.selectedProduct.step}}',
        },
      },
    ],
  } as any,
})

export const CounterProductDetailBundle: CounterProps = deepMerge(
  CounterDefault,
  {
    value: '{{catalog.currentBundleCount}}' as any,
    stock: '{{catalog.selectedProduct.stock}}' as any,
    min: '{{catalog.selectedProduct.minQtyAllowed}}' as any,
    minusButton: {
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.SET_BUNDLE_COUNT,
            args: { newValue: '{{this.newValue}}' },
          },
        ],
      } as any,
    },
    plusButton: {
      styles: {
        padding: '4px',
      },
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.SET_BUNDLE_COUNT,
            args: { newValue: '{{this.newValue}}' },
          },
        ],
      } as any,
    },
  },
)

export const CounterFreegoodsQuantitySelection: ItemCardCounterProps =
  deepMerge(CounterDefault, {
    min: 0 as any,
    minusButton: {
      styles: {
        padding: '4px',
      },
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.UPDATE_QUANTITY_SELECTION_FREEGOOD,
            args: {
              sku: '{{this.data.sku}}',
              quantity: '{{this.newValue}}',
            },
          },
        ],
      } as any,
    },
    plusButton: {
      styles: {
        padding: '4px',
      },
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.UPDATE_QUANTITY_SELECTION_FREEGOOD,
            args: {
              sku: '{{this.data.sku}}',
              quantity: '{{this.newValue}}',
              groupIdentifier: '{{this.data.groupIdentifier}}',
            },
          },
        ],
      } as any,
    },
    onInputChange: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.UPDATE_QUANTITY_SELECTION_FREEGOOD,
          args: {
            sku: '{{this.data.sku}}',
            quantity: '{{this.inRangeValue}}',
          },
        },
      ],
    } as any,
    disabledActions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.OPEN_LIMIT_DIALOG,
          args: {
            sku: '{{this.data.sku}}',
            value: '{{this.value}}',
            newValue: '{{this.newValue}}',
          },
        },
      ],
    } as any,
  })

export const CounterRecommended = deepMerge(CounterForList, {
  plusButton: {
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.ADD_ITEM_TO_CART,
          args: CounterDefaultArgs,
        },
        {
          name: TemplateActionNames.CART_UPDATE_RECOMMENDED_PRODUCT_BUTTON,
          args: {
            label: TextLibrary.RECOMMENDED_PRODUCTS_PROCEED,
          },
        },
      ],
    } as any,
  },
})

export const CounterRecommendedDetail = deepMerge(CounterProductDetail, {
  plusButton: {
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.ADD_ITEM_TO_CART,
          args: {
            ...CounterProductDetailArgs,
            referrer: Referrers.FREQUENTLY_BOUGHT_TOGETHER,
          },
        },
        {
          name: TemplateActionNames.CART_UPDATE_RECOMMENDED_PRODUCT_BUTTON,
          args: {
            label: TextLibrary.RECOMMENDED_PRODUCTS_PROCEED,
          },
        },
      ],
    } as any,
  },
  minusButton: {
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
          args: {
            ...CounterProductDetailArgs,
            referrer: Referrers.FREQUENTLY_BOUGHT_TOGETHER,
          },
        },
      ],
    } as any,
  },
})

export const CounterLinkedProducts = deepMerge(CounterForList, {
  minusButton: {
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
          args: {
            ...CounterDefaultArgs,
            referrer: Referrers.LINKED_PRODUCTS,
          },
        },
      ],
    } as any,
  },
  plusButton: {
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.ADD_ITEM_TO_CART,
          args: {
            ...CounterDefaultArgs,
            referrer: Referrers.LINKED_PRODUCTS,
          },
        },
      ],
    } as any,
  },
})
